//@ts-nocheck
import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage, getCart, isBuyNowCartSyncSource, shouldSkipBuyNowCartSync } from "@theme/utilities";
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Starts add-to-cart from click (button is type=button so the form does not submit).
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    event.preventDefault();
    const formComponent = /** @type {ProductFormComponent | null} */ (this.closest("product-form-component"));
    formComponent?.handleSubmit(event);
  }

  /**
   * Starts the fly-to-cart animation after the click has painted.
   */
  startFlyToCart() {
    const animationEnabled = this.dataset.addToCartAnimation === "true";
    if (!animationEnabled || this.closest(".quick-add-modal")) return;
    this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   * On mobile (< 750px), animates to the mobile sticky cart button.
   * On desktop, animates to the header cart icon.
   * Only animates if cart already has at least 1 item (not the first item).
   */
  async #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const image = this.dataset.productVariantMedia;

    if (!addToCartButton || !image) return;

    // Check cart item count - only animate if cart already has items (>= 1)
    try {
      const cart = await getCart();
      const cartItemCount = cart.item_count || 0;

      // Don't animate if this is the first item being added to cart
      const isMobile = window.innerWidth < 750;
      if (cartItemCount < 1 && isMobile) return;
    } catch (error) {
      console.error("Failed to fetch cart count:", error);
      // If we can't fetch cart, don't animate to be safe
      return;
    }

    // Check if we're on mobile view (matches the mobile breakpoint in CSS)
    const isMobile = window.innerWidth < 750;

    // On mobile, use the mobile sticky cart button; otherwise use desktop cart icon
    let destination;
    if (isMobile) {
      destination = document.querySelector(".mobile-sticky-cart-button__trigger");
      // Fallback to desktop cart icon if mobile button not found
      if (!destination) {
        destination = document.querySelector(".header-actions__action.header-actions__bag ");
      }
    } else {
      destination = document.querySelector(".header-actions__action.header-actions__bag");
    }

    if (!destination) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement("fly-to-cart"));

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = destination;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added");
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #addInFlight = false;

  /** @type {boolean} */
  #deferBuyButtonsSwap = false;
  /** Whether the currently selected variant can be purchased */
  #variantAvailable = true;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity(options = {}) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const { forceRefresh = false, cacheMs = 0, skipBuyButtonsUI = false } = options;
      const cart = await getCart({ forceRefresh, cacheMs });

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector("quantity-selector-component"));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update product quantity selector if it exists
      const productQuantitySelector = /** @type {any} */ (this.querySelector("product-quantity-selector-component"));
      if (productQuantitySelector?.setCartQuantity) {
        productQuantitySelector.setCartQuantity(cartQty);
      }

      if (!skipBuyButtonsUI && !this.#deferBuyButtonsSwap) {
        // Update quantity label if it exists
        this.#updateQuantityLabel(cartQty);
        // Update UI to show/hide add to cart button vs quantity selector
        this.#updateBuyButtonsUI(cartQty, { available: this.#variantAvailable });
      }

      return cartQty;
    } catch (error) {
      console.error("Failed to fetch cart quantity:", error);
      return 0;
    }
  }

  /**
   * Updates the buy buttons UI to show either Add to Cart button or quantity selector.
   * Unavailable (sold out) variants always show the ATC/Sold Out button — never the quantity UI —
   * regardless of any previous variant's cart quantity.
   * @param {number} cartQty - The quantity in cart
   * @param {{ available?: boolean }} [options]
   */
  #updateBuyButtonsUI(cartQty, options = {}) {
    const productFormButtons = this.refs.productFormButtons;
    if (!productFormButtons) return;

    const available = options.available !== undefined ? options.available : this.#variantAvailable;
    // Sold out / unavailable: always prefer ATC (Sold Out), never quantity controls
    const showQuantity = available !== false && cartQty > 0;
    const effectiveCartQty = showQuantity ? cartQty : 0;

    const addToCartWrapper = /** @type {HTMLElement | null} */ (productFormButtons.querySelector('[ref="addToCartButtonWrapper"]'));
    const quantitySelectorWrapper = /** @type {HTMLElement | null} */ (productFormButtons.querySelector('[ref="productQuantitySelectorWrapper"]'));
    const productQuantitySelector = /** @type {any} */ (productFormButtons.querySelector("product-quantity-selector-component"));

    // Show ATC button first, then hide quantity selector to prevent white space
    // Do synchronously but in correct order - browser batches DOM updates
    if (!showQuantity) {
      // Show ATC button first (synchronously)
      if (addToCartWrapper) {
        addToCartWrapper.classList.remove("hidden");
        addToCartWrapper.hidden = false;
        addToCartWrapper.setAttribute("data-cart-quantity", "0");
      }
      // Then immediately hide quantity selector (synchronously)
      if (quantitySelectorWrapper) {
        quantitySelectorWrapper.classList.add("hidden");
        quantitySelectorWrapper.hidden = true;
        quantitySelectorWrapper.setAttribute("data-cart-quantity", "0");
      }
      if (productQuantitySelector?.setCartQuantity) {
        productQuantitySelector.setCartQuantity(0);
      }
    } else {
      // Show quantity selector first (synchronously)
      if (quantitySelectorWrapper) {
        quantitySelectorWrapper.classList.remove("hidden");
        quantitySelectorWrapper.hidden = false;
        quantitySelectorWrapper.setAttribute("data-cart-quantity", effectiveCartQty.toString());
        // Update quantity selector if it exists
        if (productQuantitySelector && productQuantitySelector.setCartQuantity) {
          productQuantitySelector.setCartQuantity(effectiveCartQty);
        }
      }
      // Then immediately hide ATC button (synchronously)
      if (addToCartWrapper) {
        addToCartWrapper.classList.add("hidden");
        addToCartWrapper.hidden = true;
        addToCartWrapper.setAttribute("data-cart-quantity", effectiveCartQty.toString());
      }
    }

    // Update quantity label cart count if it exists
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      if (effectiveCartQty === 0) {
        quantityLabel.classList.add("hidden");
      } else {
        quantityLabel.classList.remove("hidden");
        const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
        if (inCartText && inCartText[2]) {
          quantityLabel.textContent = `(${effectiveCartQty} ${inCartText[2]})`;
        }
      }
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    if (shouldSkipBuyNowCartSync(event)) return;
    // Skip optimistic events — real data comes on confirmation
    if (event.detail?.data?.isOptimistic) return;

    // Check if this is for the same product
    const eventProductId = event.detail?.data?.productId;
    const thisProductId = this.dataset.productId;

    // Only sync if it's for the same product (or if productId is not specified)
    if (eventProductId && thisProductId && eventProductId !== thisProductId) {
      return;
    }

    // Determine if this component is in main form or sticky bar
    const isMainForm = !!this.closest(".buy-buttons-block");
    const isStickyBarForm = !!this.closest("product-sticky-bar");
    const eventSource = event.detail?.data?.source;
    const eventSourceId = event.detail?.sourceId;

    // Skip if this event originated from this same component instance
    // This prevents redundant cart fetches when the component dispatches its own event
    const formId = this.querySelector("form")?.getAttribute("id") || this.id;
    if (eventSource === "product-form-component" && eventSourceId === formId) {
      return;
    }

    // Enable bidirectional sync between main form and sticky bar
    // Only skip if event came from the exact same form instance (same location)
    // This allows:
    // - Main form → Sticky bar sync
    // - Sticky bar → Main form sync
    // - Sticky bar manual dispatch → Main form sync

    // Skip if event source is "product-sticky-bar" AND this is also sticky bar form
    // (sticky bar's manual dispatch should only sync main form, not itself)
    if (eventSource === "product-sticky-bar" && isStickyBarForm && !isMainForm) {
      return;
    }

    // For "product-form-component" source, allow sync to enable bidirectional sync
    // The #fetchAndUpdateCartQuantity will handle duplicate updates gracefully
    // This ensures main form and sticky bar stay in sync

    // Use requestAnimationFrame to ensure DOM is ready and prevent race conditions
    const buyNowSync = isBuyNowCartSyncSource(eventSource);
    requestAnimationFrame(async () => {
      await this.#fetchAndUpdateCartQuantity({
        forceRefresh: buyNowSync,
        cacheMs: buyNowSync ? 0 : 1000,
      });
    });
  };

  /**
   * @returns {{ addToCartWrapper: Element | null, quantitySelectorWrapper: Element | null }}
   */
  #resolveLoaderWrappers() {
    const form = this.querySelector("form");
    const atcComponent = this.refs.addToCartButtonContainer;
    let addToCartWrapper =
      atcComponent?.closest('[ref="addToCartButtonWrapper"]') ||
      form?.querySelector('[ref="addToCartButtonWrapper"]') ||
      null;
    let quantitySelectorWrapper = form?.querySelector('[ref="productQuantitySelectorWrapper"]') || null;

    if (!addToCartWrapper || !quantitySelectorWrapper) {
      const productFormButtons = this.refs.productFormButtons;
      if (productFormButtons) {
        if (!addToCartWrapper) addToCartWrapper = productFormButtons.querySelector('[ref="addToCartButtonWrapper"]');
        if (!quantitySelectorWrapper) quantitySelectorWrapper = productFormButtons.querySelector('[ref="productQuantitySelectorWrapper"]');
      }
    }

    if (!addToCartWrapper || !quantitySelectorWrapper) {
      const stickyBarContainer = this.closest("product-sticky-bar");
      if (stickyBarContainer) {
        const desktopButtons = stickyBarContainer.querySelector(".product-sticky-bar__buttons");
        if (desktopButtons) {
          if (!addToCartWrapper) addToCartWrapper = desktopButtons.querySelector('[ref="addToCartButtonWrapper"]');
          if (!quantitySelectorWrapper) quantitySelectorWrapper = desktopButtons.querySelector('[ref="productQuantitySelectorWrapper"]');
        }
        const mobileActions = stickyBarContainer.querySelector(".product-sticky-bar__mobile-actions");
        if (mobileActions) {
          const mobileForm = mobileActions.querySelector("form");
          if (mobileForm) {
            if (!addToCartWrapper) addToCartWrapper = mobileForm.querySelector('[ref="addToCartButtonWrapper"]');
            if (!quantitySelectorWrapper) quantitySelectorWrapper = mobileForm.querySelector('[ref="productQuantitySelectorWrapper"]');
          }
        }
      }
    }

    return { addToCartWrapper, quantitySelectorWrapper };
  }

  #showLoaders() {
    const { addToCartWrapper, quantitySelectorWrapper } = this.#resolveLoaderWrappers();
    const wrapperHidden = addToCartWrapper?.classList.contains("hidden");
    const quantitySelectorLoader = quantitySelectorWrapper?.querySelector('[ref="quantitySelectorLoader"]');

    if (!wrapperHidden) {
      addToCartWrapper?.querySelector('[ref="addToCartLoader"]')?.classList.remove("hidden");
      this.refs.addToCartButtonContainer?.querySelector('[ref="addToCartLoader"]')?.classList.remove("hidden");
    }
    if (quantitySelectorLoader && !quantitySelectorWrapper?.classList.contains("hidden")) {
      quantitySelectorLoader.classList.remove("hidden");
    }
  }

  #hideLoaders() {
    const { addToCartWrapper, quantitySelectorWrapper } = this.#resolveLoaderWrappers();
    addToCartWrapper?.querySelector('[ref="addToCartLoader"]')?.classList.add("hidden");
    this.refs.addToCartButtonContainer?.querySelector('[ref="addToCartLoader"]')?.classList.add("hidden");
    quantitySelectorWrapper?.querySelector('[ref="quantitySelectorLoader"]')?.classList.add("hidden");
  }

  #releaseAddLock() {
    this.#addInFlight = false;
    this.refs.addToCartButtonContainer?.enable();
  }

  /**
   * Starts add-to-cart from a click (preferred) or leftover form submit (e.g. Enter).
   * Always preventDefault so a native submit never reaches GTM.
   *
   * @param {Event} event - Click or submit event.
   */
  handleSubmit(event) {
    event.preventDefault();

    // Never treat Quick Buy "open modal" controls as add-to-cart
    const submitter = /** @type {HTMLElement | null} */ (
      /** @type {SubmitEvent} */ (event).submitter ||
        (event.target instanceof Element ? event.target.querySelector?.(".quick-add__button--choose, [data-quick-buy-open]") : null)
    );
    if (
      submitter?.classList?.contains("quick-add__button--choose") ||
      submitter?.hasAttribute?.("data-quick-buy-open") ||
      this.querySelector(".quick-add__button--choose:focus, [data-quick-buy-open]:focus")
    ) {
      return;
    }

    const { addToCartButtonContainer } = this.refs;
    if (this.#addInFlight) return;
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    const form = this.querySelector("form");
    if (!form) throw new Error("Product form element missing");

    // Linked-product override from Quick Buy modal (set by linked-products-picker.js)
    const linkedOverrideId = this.dataset.linkedOverrideVariantId;
    if (linkedOverrideId) {
      let idInput = form.querySelector('input[name="id"]');
      if (!(idInput instanceof HTMLInputElement)) {
        idInput = document.createElement("input");
        idInput.type = "hidden";
        idInput.name = "id";
        form.appendChild(idInput);
      }
      idInput.disabled = false;
      idInput.value = String(linkedOverrideId);
      idInput.setAttribute("value", String(linkedOverrideId));
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    this.#addInFlight = true;
    setTimeout(()=>{
      addToCartButtonContainer?.disable();
    },0);
    if (!this.classList.contains("quick-add__product-form-component")) {
      this.#showLoaders();
    }

    setTimeout(() => {
      this.#runAddToCart(event, form);
    }, 0);
  }

  /**
   * Runs cart add after the click has painted (loader already visible).
   *
   * @param {Event} event
   * @param {HTMLFormElement} form
   */
  async #runAddToCart(event, form) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;

    const quantitySelector = /** @type {any} */ (this.querySelector("quantity-selector-component"));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        this.#hideLoaders();

        const errorTemplate = this.dataset.quantityErrorMax || "";
        const errorMessage = errorTemplate.replace("{{ maximum }}", validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove("hidden");

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          this.#releaseAddLock();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    }

    // JSON body so numeric line-item props stay numbers (FormData always stringifies).
    const cartAddPayload = this.#formDataToCartAddPayload(formData);
    const fetchCfg = fetchConfig("javascript", { body: JSON.stringify(cartAddPayload) });
    // Re-apply JSON content-type (fetchConfig("javascript") strips it for FormData).
    fetchCfg.headers = {
      ...fetchCfg.headers,
      "Content-Type": "application/json",
    };

    // --- OPTIMISTIC UI: Instantly update cart count & quantity before the fetch ---
    const quantityToAdd = Number(cartAddPayload.quantity) || Number(this.dataset.quantityDefault) || 1;
    const formId = form.getAttribute("id") || this.id;

    this.dispatchEvent(
      new CartAddEvent(
        {},
        formId,
        /** @type {any} */ ({
          source: "product-form-component",
          itemCount: quantityToAdd,
          productId: this.dataset.productId,
          isOptimistic: true,
        })
      )
    );

    const currentCartQty = parseInt(
      quantitySelector?.refs?.quantityInput?.getAttribute("data-cart-quantity") || "0"
    );
    const optimisticCartQty = currentCartQty + quantityToAdd;

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(optimisticCartQty);
    }
    const productQuantitySelector = /** @type {any} */ (this.querySelector("product-quantity-selector-component"));
    if (productQuantitySelector?.setCartQuantity) {
      productQuantitySelector.setCartQuantity(optimisticCartQty);
    }

    addToCartButtonContainer?.startFlyToCart?.();

    if (this.classList.contains("quick-add__product-form-component")) {
      addToCartButtonContainer?.animateAddToCart();
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.#hideLoaders();
          if (this.classList.contains("quick-add__product-form-component")) {
            addToCartButtonContainer?.refs.addToCartButton?.classList.remove("atc-added");
          }
          this.#deferBuyButtonsSwap = false;
          this.dispatchEvent(new CartErrorEvent(form.getAttribute("id") || "", response.message, response.description, response.errors));

          try {
            const realCart = await getCart({ forceRefresh: true });

            const cartIconEl = /** @type {any} */ (document.querySelector("cart-icon"));
            if (cartIconEl?.renderCartBubble) {
              cartIconEl.renderCartBubble(realCart.item_count, false, false);
            }

            const mobileBtn = /** @type {any} */ (document.querySelector("mobile-sticky-cart-button"));
            if (mobileBtn?.renderCartCount) {
              mobileBtn.renderCartCount(realCart.item_count);
            }

            await this.#fetchAndUpdateCartQuantity({ forceRefresh: true });
          } catch (_rollbackError) {
            console.error("Failed to rollback optimistic cart update:", _rollbackError);
          }

          this.#releaseAddLock();

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        const id = formData.get("id");

        if (addToCartTextError) {
          addToCartTextError.classList.add("hidden");
          addToCartTextError.removeAttribute("aria-live");
        }

        if (!id) throw new Error("Form ID is required");

        this.#hideLoaders();
        this.#deferBuyButtonsSwap = true;
        if (!this.classList.contains("quick-add__product-form-component")) {
          addToCartButtonContainer?.animateAddToCart();
        }

        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
          const addedTextElement = addToCartButton.querySelector(".add-to-cart-text--added");
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const productVariantMedia = this.refs.addToCartButtonContainer?.dataset.productVariantMedia || null;

        const cartQty = await this.#fetchAndUpdateCartQuantity({ forceRefresh: true, skipBuyButtonsUI: true });
        const confirmedCart = await getCart();

        const confirmFormId = form.getAttribute("id") || this.id || id.toString();
        const confirmEventData = /** @type {any} */ ({
          source: "product-form-confirmation",
          itemCount: confirmedCart.item_count,
          productId: this.dataset.productId,
          variantId: id.toString(),
          sections: response.sections,
          productVariantMedia: productVariantMedia,
        });

        const quantityAdded = Number(formData.get("quantity")) || Number(this.dataset.quantityDefault) || 1;
        let productDataFromForm = null;
        try {
          const raw = this.dataset.productGtm;
          if (raw) productDataFromForm = JSON.parse(raw);
        } catch (_) {}
        const gtmPayload = {
          ...(productDataFromForm && typeof productDataFromForm === "object" ? productDataFromForm : {}),
          product_id: this.dataset.productId ?? null,
          product_variant_id: id.toString(),
          total_quantity: quantityAdded,
          cta_name: "add_to_bag",
          triggerElement: this,
        };
        setTimeout(() => {
          (/** @type {Window & { addToCartGtm?: (data?: Record<string, unknown>) => void }} */ (window)).addToCartGtm?.(gtmPayload);
        }, 0);

        setTimeout(() => {
          this.#deferBuyButtonsSwap = false;
          this.dispatchEvent(new CartAddEvent({}, confirmFormId, confirmEventData));
          this.#updateBuyButtonsUI(cartQty);
          this.#updateQuantityLabel(cartQty);
          this.#releaseAddLock();
        }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
      })
      .catch(async (error) => {
        console.error(error);
        this.#hideLoaders();
        if (this.classList.contains("quick-add__product-form-component")) {
          addToCartButtonContainer?.refs.addToCartButton?.classList.remove("atc-added");
        }
        this.#deferBuyButtonsSwap = false;

        try {
          const realCart = await getCart({ forceRefresh: true });

          const cartIconEl = /** @type {any} */ (document.querySelector("cart-icon"));
          if (cartIconEl?.renderCartBubble) {
            cartIconEl.renderCartBubble(realCart.item_count, false, false);
          }

          const mobileBtn = /** @type {any} */ (document.querySelector("mobile-sticky-cart-button"));
          if (mobileBtn?.renderCartCount) {
            mobileBtn.renderCartCount(realCart.item_count);
          }

          await this.#fetchAndUpdateCartQuantity({ forceRefresh: true });
        } catch (_) {
          // Best-effort rollback
        }

        this.#releaseAddLock();
      })
      .finally(() => {
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle("hidden", cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement("beforebegin", /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? "";

    const isAvailable = event.detail.resource != null && event.detail.resource.available !== false;
    this.#variantAvailable = isAvailable;

    // Sold out must replace quantity UI immediately — don't wait on cart fetch
    if (!isAvailable) {
      this.#updateBuyButtonsUI(0, { available: false });
    }

    // Update the button state (even if we later return early for missing checkout refs)
    if (currentAddToCartButton) {
      if (!isAvailable) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (!isAvailable) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
      }
    }

    // Still update cart/qty UI even when ATC refs are temporarily missing
    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) {
      await this.#fetchAndUpdateCartQuantity();
      return;
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia && addToCartButtonContainer?.setAttribute("data-product-variant-media", productVariantMedia + "&width=100");
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector("quantity-selector-component"));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]'));

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    // Update product quantity selector's variant ID, constraints, and digital-product state
    const productQuantitySelector = /** @type {any} */ (this.querySelector("product-quantity-selector-component"));
    const newProductQuantitySelector = event.detail.data.html.querySelector("product-quantity-selector-component");
    const newProductQuantityInput = /** @type {HTMLInputElement | null} */ (event.detail.data.html.querySelector('product-quantity-selector-component input[ref="quantityInput"]'));
    const isDigitalProduct = newProductQuantitySelector?.getAttribute("data-is-digital-product") === "true";

    // Keep add-to-cart component and line item properties in sync with digital / physical variant state
    const formEl = /** @type {HTMLFormElement | null} */ (this.querySelector("form"));
    if (formEl) {
      this.#upsertFormProperty(formEl, "_digital_book", isDigitalProduct ? "true" : "false");

      // Variant-dependent cart line properties
      const resource = event.detail.resource;
      if (resource) {
        const qty =
          resource.inventory_quantity != null ? String(resource.inventory_quantity) : "0";
        this.#upsertFormProperty(formEl, "_product_available_quantity", qty);

        const compareAt =
          resource.compare_at_price != null && resource.compare_at_price !== ""
            ? String(resource.compare_at_price)
            : "";
        this.#upsertFormProperty(formEl, "_product_price", compareAt);
      }
    }

    // Also keep the add-to-cart component data attribute updated for other consumers
    if (addToCartButtonContainer) {
      addToCartButtonContainer.setAttribute("data-is-digital-product", isDigitalProduct ? "true" : "false");
    }

    if (productQuantitySelector?.updateVariant && newProductQuantityInput && event.detail.resource?.id) {
      productQuantitySelector.updateVariant(event.detail.resource.id.toString(), newProductQuantityInput.min || "1", newProductQuantityInput.max || null, newProductQuantityInput.step || "1", isDigitalProduct);
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector(".quantity-rules");
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector(".product-form-buttons");

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector("quantity-selector-component"));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]'));

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(newQuantityInputElement.min, newQuantityInputElement.max || null, newQuantityInputElement.step);
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector(".quantity-label");
      const newQuantityLabel = event.detail.data.html.querySelector(".quantity-label");
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Build /cart/add.js JSON payload from form fields.
   * Coerces `_product_available_quantity` and `_product_price` to numbers.
   * @param {FormData} formData
   * @returns {Record<string, any>}
   */
  #formDataToCartAddPayload(formData) {
    const NUMERIC_PROP_KEYS = new Set(["_product_available_quantity", "_product_price"]);
    const IGNORE_KEYS = new Set(["form_type", "utf8", "product-id", "section-id"]);
    /** @type {Record<string, string | number>} */
    const properties = {};
    /** @type {Record<string, any>} */
    const payload = {
      quantity: 1,
      properties,
    };

    for (const [key, rawValue] of formData.entries()) {
      if (IGNORE_KEYS.has(key)) continue;
      const value = typeof rawValue === "string" ? rawValue : String(rawValue);

      if (key === "id") {
        payload.id = value;
        continue;
      }
      if (key === "quantity") {
        const qty = Number(value);
        payload.quantity = Number.isFinite(qty) && qty > 0 ? qty : 1;
        continue;
      }
      if (key === "sections") {
        payload.sections = value;
        continue;
      }
      if (key === "selling_plan") {
        payload.selling_plan = value;
        continue;
      }

      const propMatch = /^properties\[(.+)\]$/.exec(key);
      if (!propMatch) continue;
      const propKey = propMatch[1];
      if (NUMERIC_PROP_KEYS.has(propKey)) {
        if (value === "") {
          properties[propKey] = 0;
        } else {
          const num = Number(value);
          properties[propKey] = Number.isFinite(num) ? num : 0;
        }
      } else {
        properties[propKey] = value;
      }
    }

    return payload;
  }

  /**
   * Create or update a hidden `properties[...]` input on the add-to-cart form.
   * @param {HTMLFormElement} formEl
   * @param {string} key - Property key without the properties[] wrapper (e.g. `_digital_book`)
   * @param {string} value
   */
  #upsertFormProperty(formEl, key, value) {
    const name = `properties[${key}]`;
    /** @type {HTMLInputElement | null} */
    let input = formEl.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      const idInput = formEl.querySelector('input[name="id"]');
      if (idInput && idInput.parentElement === formEl) {
        idInput.insertAdjacentElement("afterend", input);
      } else {
        formEl.appendChild(input);
      }
    }
    input.value = value;
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 1000; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
